import React, { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import {
  Bold as BoldIcon, Italic as ItalicIcon, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, Link as LinkIcon, Image as ImageIcon,
  Minus, Undo2, Redo2, Plus, Send,
} from 'lucide-react';
import Modal from './Modal';
import TokenPickerPopover from './TokenPickerPopover';
import TemplatePreviewPane from './TemplatePreviewPane';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

// Sprint F item 6.5 — TipTap-based template editor with token picker + live
// preview + send-test. Two-pane layout: left WYSIWYG, right preview.
//
// Caller controls `subject`, `bodyHtml`, `bodyText`, `customTokens`, plus the
// callbacks for change. The editor auto-derives plain text from rich text on
// save; admin can override via the "Edit plain text manually" toggle.

function htmlToText(html) {
  return String(html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<[^>]*>/g, '')
    .replace(/\s+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export default function TemplateEditor({
  subject,
  onSubjectChange,
  bodyHtml,
  onBodyHtmlChange,
  bodyText,
  onBodyTextChange,
  customTokens = [],
  onAddCustomToken,
  sender,
  campaignId,
  // Broadcasts route the test through the chosen Gmail alias instead of the
  // campaign test-send endpoint: ({ subject, bodyHtml, bodyText }) → { to, school? }.
  onSendTest = null,
  testHint = null,
}) {
  // Test sends always go to the logged-in staff user's own inbox
  // (see functions/api/admin/outreach/test-send.js), so the result text
  // names that address rather than a configurable recipient.
  const { user } = useAuth();
  const [pickerTarget, setPickerTarget] = useState(null);
  const [editText, setEditText] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testStatus, setTestStatus] = useState('');
  // The school selected in the preview's "Preview as" (null = the sample);
  // "Send test" renders that school's version.
  const [previewAccount, setPreviewAccount] = useState(null);
  const subjectInputRef = useRef(null);
  // Capture the caret/selection at the moment the picker is opened, so we
  // can restore it on insert. Without this, opening the modal moves focus
  // away from the editor — TipTap's selection survives but `.focus()` on
  // an editor that lost focus can land the caret at position 0, and the
  // subject <input> loses its selectionStart entirely.
  const savedSelectionRef = useRef(null);

  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' } }),
      Image.configure({ inline: false, allowBase64: false, HTMLAttributes: { style: 'max-width: 100%; height: auto;' } }),
    ],
    content: bodyHtml || '<p></p>',
    onUpdate: ({ editor }) => {
      const next = editor.getHTML();
      onBodyHtmlChange?.(next);
      if (!editText) onBodyTextChange?.(htmlToText(next));
    },
  });

  useEffect(() => {
    if (editor && bodyHtml != null && bodyHtml !== editor.getHTML()) {
      editor.commands.setContent(bodyHtml || '<p></p>', false);
    }
  }, [editor, bodyHtml]);

  const openPickerForSubject = () => {
    const el = subjectInputRef.current;
    savedSelectionRef.current = {
      target: 'subject',
      start: el?.selectionStart ?? (subject?.length ?? 0),
      end: el?.selectionEnd ?? (subject?.length ?? 0),
    };
    setPickerTarget('subject');
  };

  const openPickerForBody = () => {
    if (editor) {
      const { from, to } = editor.state.selection;
      savedSelectionRef.current = { target: 'body', from, to };
    } else {
      savedSelectionRef.current = { target: 'body', from: null, to: null };
    }
    setPickerTarget('body');
  };

  const insertToken = (name) => {
    const literal = `{{${name}}}`;
    const saved = savedSelectionRef.current;
    if (pickerTarget === 'subject') {
      const start = saved?.start ?? subject?.length ?? 0;
      const end = saved?.end ?? start;
      const cur = subject || '';
      const next = cur.slice(0, start) + literal + cur.slice(end);
      onSubjectChange?.(next);
      // Restore caret after the inserted token on next tick.
      setTimeout(() => {
        const el = subjectInputRef.current;
        if (el) {
          const pos = start + literal.length;
          el.focus();
          try { el.setSelectionRange(pos, pos); } catch (e) { /* noop */ }
        }
      }, 0);
    } else if (editor) {
      const chain = editor.chain().focus();
      if (saved?.target === 'body' && saved.from != null) {
        chain.setTextSelection({ from: saved.from, to: saved.to ?? saved.from });
      }
      chain.insertContent(literal).run();
    }
    setPickerTarget(null);
  };

  const sendTest = async () => {
    if (!editor) return;
    setSendingTest(true);
    setTestStatus('');
    if (onSendTest) {
      try {
        const r = await onSendTest({ subject, bodyHtml: editor.getHTML(), bodyText: editText ? bodyText : htmlToText(editor.getHTML()), previewAccount });
        setTestStatus(r?.to ? `Test sent to ${r.to}${r.school ? ` — ${r.school}’s version` : ''}` : 'Test sent');
      } catch (err) {
        setTestStatus(err?.data?.error || err?.message || 'Test send failed');
      } finally {
        setSendingTest(false);
      }
      return;
    }
    try {
      const r = await api.post('/api/admin/outreach/test-send', {
        subject_template: subject,
        body_template_html: editor.getHTML(),
        body_template_text: editText ? bodyText : htmlToText(editor.getHTML()),
        custom_tokens: Object.fromEntries((customTokens || []).map((c) => [c.key, c.value])),
        ...(previewAccount?.id ? { account_id: previewAccount.id } : {}),
      });
      if (r?.skipped) {
        setTestStatus('Test not sent — email is in stub mode in this environment.');
      } else {
        const to = r?.to || user?.email;
        const whose = r?.sample ? 'sample school' : r?.school;
        setTestStatus(to ? `Test sent to ${to}${whose ? ` — ${whose}’s version` : ''}` : 'Test sent');
      }
    } catch (err) {
      setTestStatus(err?.data?.error || err?.message || 'Test send failed');
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div data-testid="template-editor" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Editor pane */}
      <div className="space-y-3">
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-gray-400/80 mb-1.5">Subject</label>
          <div className="flex items-center gap-1.5">
            <input
              ref={subjectInputRef}
              type="text"
              data-testid="template-subject-input"
              value={subject || ''}
              onChange={(e) => onSubjectChange?.(e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white"
            />
            <button
              type="button"
              data-testid="template-subject-token"
              onClick={openPickerForSubject}
              className="px-2 py-2 rounded-lg text-[11px] text-gray-300 hover:text-white border border-white/[0.08]"
            >
              + Token
            </button>
          </div>
        </div>

        <div className="rounded-lg border border-white/[0.08] bg-white/[0.02]">
          <Toolbar
            editor={editor}
            onInsertToken={openPickerForBody}
            onOpenLink={() => setLinkModalOpen(true)}
            onOpenImage={() => setImageModalOpen(true)}
          />
          {/*
            Same reasoning as TemplatePreviewPane: Tailwind preflight strips
            list markers and the project doesn't ship @tailwindcss/typography,
            so we restore them via a scoped style block on the editor pane
            so users see bullets/numbers as they type.
          */}
          <style>{`
            .tiptap-editor p { margin: 0 0 0.5em 0; }
            .tiptap-editor ul { list-style: disc; padding-left: 1.5em; margin: 0.5em 0; }
            .tiptap-editor ol { list-style: decimal; padding-left: 1.5em; margin: 0.5em 0; }
            .tiptap-editor li { margin: 0.25em 0; }
            .tiptap-editor li > p { margin: 0; }
            .tiptap-editor strong { font-weight: 600; }
            .tiptap-editor em { font-style: italic; }
            .tiptap-editor u { text-decoration: underline; }
            .tiptap-editor s { text-decoration: line-through; }
            .tiptap-editor a { color: #93c5fd; text-decoration: underline; }
            .tiptap-editor h1 { font-size: 1.5em; font-weight: 600; margin: 0.75em 0 0.5em; }
            .tiptap-editor h2 { font-size: 1.25em; font-weight: 600; margin: 0.75em 0 0.5em; }
            .tiptap-editor h3 { font-size: 1.1em; font-weight: 600; margin: 0.75em 0 0.5em; }
            .tiptap-editor hr { border: 0; border-top: 1px solid rgba(255,255,255,0.15); margin: 1em 0; }
            .tiptap-editor img { max-width: 100%; height: auto; border-radius: 4px; }
            .tiptap-editor img.ProseMirror-selectednode { outline: 2px solid #B0FC38; }
            .tiptap-editor :focus { outline: none; }
          `}</style>
          <div className="tiptap-editor px-3 py-3 max-w-none text-sm text-white" data-testid="template-body-editor">
            <EditorContent editor={editor} />
          </div>
        </div>

        <details className="rounded-lg border border-white/[0.06] bg-white/[0.02]">
          <summary
            data-testid="template-edit-text-toggle"
            className="cursor-pointer px-3 py-2 text-[12px] text-gray-300"
            onClick={() => setEditText((v) => !v)}
          >
            {editText ? 'Hide plain-text edit' : 'Edit plain text manually'}
          </summary>
          <textarea
            data-testid="template-body-text"
            value={editText ? (bodyText || htmlToText(bodyHtml || '')) : htmlToText(bodyHtml || '')}
            onChange={(e) => editText && onBodyTextChange?.(e.target.value)}
            disabled={!editText}
            rows={6}
            className="w-full px-3 py-2 bg-transparent text-sm text-white outline-none disabled:text-gray-500"
          />
        </details>

        {/* The hint can be a full sentence ("… is not in this broadcast. The test
            sends …’s version"): it wraps; the button keeps its shape. */}
        <div className="flex items-center justify-between gap-3">
          <div data-testid="template-test-status" className="min-w-0 text-[11px] leading-snug text-gray-400">
            {testStatus || (typeof testHint === 'function' ? testHint(previewAccount) : testHint) || (onSendTest ? '' : 'Uses the school selected in the preview.')}
          </div>
          <button
            type="button"
            data-testid="template-send-test"
            onClick={sendTest}
            disabled={sendingTest}
            className="shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#B0FC38]/15 border border-[#B0FC38]/40 text-[12px] text-white hover:bg-[#B0FC38]/25 disabled:opacity-50"
          >
            <Send className="w-3 h-3" /> {sendingTest ? 'Sending…' : 'Send test'}
          </button>
        </div>

      </div>

      <TokenPickerPopover
        open={pickerTarget != null}
        target={pickerTarget}
        customTokens={customTokens}
        onPick={insertToken}
        onClose={() => setPickerTarget(null)}
        onAddCustom={(t) => { onAddCustomToken?.(t); setPickerTarget(null); }}
      />

      {linkModalOpen && (
        <LinkPromptModal
          editor={editor}
          initialHref={editor?.getAttributes('link')?.href || ''}
          initialText={(() => {
            if (!editor) return '';
            const { from, to } = editor.state.selection;
            return editor.state.doc.textBetween(from, to, ' ');
          })()}
          onClose={() => setLinkModalOpen(false)}
        />
      )}
      {imageModalOpen && (
        <ImagePromptModal
          editor={editor}
          onClose={() => setImageModalOpen(false)}
        />
      )}

      {/* Preview pane */}
      <TemplatePreviewPane
        subjectTemplate={subject}
        bodyHtml={editor?.getHTML() || bodyHtml}
        sender={sender}
        customTokens={customTokens}
        campaignId={campaignId}
        onAccountChange={setPreviewAccount}
      />
    </div>
  );
}

function Toolbar({ editor, onInsertToken, onOpenLink, onOpenImage }) {
  if (!editor) return <div className="px-2 py-2 border-b border-white/[0.04]" />;
  const Btn = ({ active, onClick, children, label, testId, disabled }) => (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      title={label}
      disabled={disabled}
      className={`p-1.5 rounded ${active ? 'bg-[#B0FC38]/20 text-white' : 'text-gray-400 hover:text-white disabled:text-gray-700 disabled:hover:text-gray-700'}`}
    >
      {children}
    </button>
  );
  const block = editor.isActive('heading', { level: 1 })
    ? 'h1'
    : editor.isActive('heading', { level: 2 })
      ? 'h2'
      : editor.isActive('heading', { level: 3 })
        ? 'h3'
        : 'p';
  const setBlock = (val) => {
    if (val === 'p') editor.chain().focus().setParagraph().run();
    else if (val === 'h1') editor.chain().focus().setHeading({ level: 1 }).run();
    else if (val === 'h2') editor.chain().focus().setHeading({ level: 2 }).run();
    else if (val === 'h3') editor.chain().focus().setHeading({ level: 3 }).run();
  };
  return (
    <div className="flex items-center gap-1 px-2 py-1.5 border-b border-white/[0.04] bg-white/[0.01] flex-wrap">
      <select
        value={block}
        onChange={(e) => setBlock(e.target.value)}
        title="Paragraph style"
        data-testid="template-toolbar-block"
        className="text-[11px] bg-transparent text-gray-300 hover:text-white border border-white/[0.08] rounded px-1.5 py-1 outline-none"
      >
        <option value="p">Paragraph</option>
        <option value="h1">Heading 1</option>
        <option value="h2">Heading 2</option>
        <option value="h3">Heading 3</option>
      </select>
      <span className="w-px h-4 bg-white/[0.08] mx-1" />
      <Btn label="Bold" testId="template-toolbar-bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><BoldIcon className="w-3.5 h-3.5" /></Btn>
      <Btn label="Italic" testId="template-toolbar-italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><ItalicIcon className="w-3.5 h-3.5" /></Btn>
      <Btn label="Underline" testId="template-toolbar-underline" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon className="w-3.5 h-3.5" /></Btn>
      <Btn label="Strikethrough" testId="template-toolbar-strike" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough className="w-3.5 h-3.5" /></Btn>
      <span className="w-px h-4 bg-white/[0.08] mx-1" />
      <Btn label="Bulleted list" testId="template-toolbar-bullet" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="w-3.5 h-3.5" /></Btn>
      <Btn label="Numbered list" testId="template-toolbar-numbered" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="w-3.5 h-3.5" /></Btn>
      <span className="w-px h-4 bg-white/[0.08] mx-1" />
      <Btn label="Link" testId="template-toolbar-link" active={editor.isActive('link')} onClick={onOpenLink}><LinkIcon className="w-3.5 h-3.5" /></Btn>
      <Btn label="Image" testId="template-toolbar-image" onClick={onOpenImage}><ImageIcon className="w-3.5 h-3.5" /></Btn>
      <Btn label="Horizontal rule" testId="template-toolbar-hr" onClick={() => editor.chain().focus().setHorizontalRule().run()}><Minus className="w-3.5 h-3.5" /></Btn>
      <span className="w-px h-4 bg-white/[0.08] mx-1" />
      <Btn label="Undo" testId="template-toolbar-undo" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}><Undo2 className="w-3.5 h-3.5" /></Btn>
      <Btn label="Redo" testId="template-toolbar-redo" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}><Redo2 className="w-3.5 h-3.5" /></Btn>
      <span className="w-px h-4 bg-white/[0.08] mx-1" />
      <button
        type="button"
        data-testid="template-toolbar-token"
        onClick={onInsertToken}
        className="px-2 py-1 rounded text-[11px] text-gray-300 hover:text-white hover:bg-white/[0.04] inline-flex items-center gap-1"
      >
        <Plus className="w-3 h-3" /> Token
      </button>
    </div>
  );
}

function normalizeUrl(raw) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return '';
  // Allow mailto: and tel: as-is. Otherwise auto-prepend https:// if no scheme.
  if (/^(https?:|mailto:|tel:)/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function LinkPromptModal({ editor, initialHref, initialText, onClose }) {
  const [href, setHref] = useState(initialHref || '');
  const [text, setText] = useState(initialText || '');
  const isUpdate = !!initialHref;

  const apply = () => {
    if (!editor) return onClose();
    const url = normalizeUrl(href);
    if (!url) return;
    const { from, to } = editor.state.selection;
    const hasSelection = from !== to;
    const chain = editor.chain().focus();
    if (hasSelection) {
      // Apply link to existing selection. If text differs from current
      // selection, replace the selection with the new text.
      const current = editor.state.doc.textBetween(from, to, ' ');
      if (text && text !== current) {
        chain.insertContentAt({ from, to }, text).run();
        const newTo = from + text.length;
        editor.chain().focus().setTextSelection({ from, to: newTo }).setLink({ href: url }).run();
      } else {
        chain.setLink({ href: url }).run();
      }
    } else {
      // No selection — insert the link text and apply the mark.
      const insert = text || url;
      editor.chain().focus()
        .insertContent(`<a href="${url}">${insert.replace(/</g, '&lt;')}</a>`)
        .run();
    }
    onClose();
  };

  const remove = () => {
    if (!editor) return onClose();
    editor.chain().focus().unsetLink().run();
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={isUpdate ? 'Edit link' : 'Insert link'}
      maxWidth="max-w-md"
      footer={
        <div className="flex items-center justify-between gap-3 w-full">
          {isUpdate ? (
            <button onClick={remove} className="px-3 py-1.5 text-[12px] text-red-400 hover:text-red-300">Remove link</button>
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-full border border-white/[0.1] text-sm text-gray-300 hover:text-white">Cancel</button>
            <button onClick={apply} disabled={!href.trim()} className="px-5 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] disabled:opacity-50">
              {isUpdate ? 'Save' : 'Insert'}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        <label className="block">
          <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5">URL</div>
          <input
            autoFocus
            type="text"
            value={href}
            onChange={(e) => setHref(e.target.value)}
            placeholder="https://example.com"
            className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white"
            onKeyDown={(e) => { if (e.key === 'Enter' && href.trim()) apply(); }}
          />
          <div className="text-[10px] text-gray-500 mt-1">Auto-prefixed with https:// if no scheme. mailto: and tel: also supported.</div>
        </label>
        <label className="block">
          <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5">Display text (optional)</div>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Click here"
            className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white"
          />
          <div className="text-[10px] text-gray-500 mt-1">Leave blank to use the URL as the link text.</div>
        </label>
      </div>
    </Modal>
  );
}

function ImagePromptModal({ editor, onClose }) {
  const [tab, setTab] = useState('upload'); // 'upload' | 'url'
  const [src, setSrc] = useState('');
  const [alt, setAlt] = useState('');
  const [width, setWidth] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const uploadFile = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|gif|webp)$/i.test(file.type)) {
      setUploadError(`Unsupported file type: ${file.type || 'unknown'}. Allowed: PNG, JPEG, GIF, WebP.`);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError(`File too large: ${Math.round(file.size / 1024)}KB. Max 5MB.`);
      return;
    }
    setUploading(true); setUploadError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/admin/outreach/images', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || `Upload failed (${res.status})`);
      }
      const data = await res.json();
      setSrc(data.url || '');
      if (!alt && file.name) setAlt(file.name.replace(/\.[^.]+$/, ''));
    } catch (err) {
      setUploadError(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const apply = () => {
    if (!editor) return onClose();
    const url = tab === 'url' ? normalizeUrl(src) : src.trim();
    if (!url) return;
    const attrs = { src: url };
    if (alt.trim()) attrs.alt = alt.trim();
    if (width.trim()) {
      const w = parseInt(width, 10);
      if (Number.isFinite(w) && w > 0) {
        attrs.style = `max-width: 100%; height: auto; width: ${w}px;`;
      }
    }
    editor.chain().focus().setImage(attrs).run();
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Insert image"
      maxWidth="max-w-md"
      footer={
        <div className="flex items-center justify-end gap-3 w-full">
          <button onClick={onClose} className="px-4 py-2 rounded-full border border-white/[0.1] text-sm text-gray-300 hover:text-white">Cancel</button>
          <button onClick={apply} disabled={!src.trim() || uploading} className="px-5 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] disabled:opacity-50">
            Insert
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="flex items-center gap-1 p-0.5 bg-white/[0.03] border border-white/[0.06] rounded-lg w-fit">
          <button
            type="button"
            onClick={() => setTab('upload')}
            className={`px-3 py-1 text-[12px] rounded ${tab === 'upload' ? 'bg-white/[0.08] text-white' : 'text-gray-400 hover:text-white'}`}
          >
            Upload from device
          </button>
          <button
            type="button"
            onClick={() => setTab('url')}
            className={`px-3 py-1 text-[12px] rounded ${tab === 'url' ? 'bg-white/[0.08] text-white' : 'text-gray-400 hover:text-white'}`}
          >
            Paste URL
          </button>
        </div>

        {tab === 'upload' ? (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              onChange={onPickFile}
              className="hidden"
            />
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className={`cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
                dragOver ? 'border-[#B0FC38] bg-[#B0FC38]/10' : 'border-white/[0.1] hover:border-white/[0.2] bg-white/[0.02]'
              }`}
            >
              {uploading ? (
                <div className="text-[12px] text-gray-300">Uploading…</div>
              ) : src && tab === 'upload' ? (
                <div className="space-y-2">
                  <img src={src} alt="" className="max-h-32 mx-auto rounded" />
                  <div className="text-[11px] text-gray-400 truncate">{src}</div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setSrc(''); }}
                    className="text-[11px] text-gray-500 hover:text-white underline"
                  >
                    Choose a different file
                  </button>
                </div>
              ) : (
                <>
                  <div className="text-[13px] text-gray-300">Click to choose a file</div>
                  <div className="text-[11px] text-gray-500 mt-1">or drag and drop here</div>
                  <div className="text-[10px] text-gray-600 mt-2">PNG, JPEG, GIF, WebP · max 5MB</div>
                </>
              )}
            </div>
            {uploadError && (
              <div className="mt-2 text-[11px] text-red-300 bg-red-500/10 border border-red-500/20 rounded px-2 py-1">
                {uploadError}
              </div>
            )}
          </div>
        ) : (
          <label className="block">
            <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5">Image URL</div>
            <input
              autoFocus
              type="text"
              value={src}
              onChange={(e) => setSrc(e.target.value)}
              placeholder="https://example.com/image.png"
              className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white"
              onKeyDown={(e) => { if (e.key === 'Enter' && src.trim()) apply(); }}
            />
            <div className="text-[10px] text-gray-500 mt-1">Must be publicly accessible. Email clients fetch the image when the recipient opens the email.</div>
          </label>
        )}

        <label className="block">
          <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5">Alt text (recommended)</div>
          <input
            type="text"
            value={alt}
            onChange={(e) => setAlt(e.target.value)}
            placeholder="Describe the image for screen readers / blocked-image fallback"
            className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white"
          />
        </label>
        <label className="block max-w-[180px]">
          <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5">Display width (px)</div>
          <input
            type="number"
            min="10"
            value={width}
            onChange={(e) => setWidth(e.target.value)}
            placeholder="e.g. 480"
            className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white"
          />
          <div className="text-[10px] text-gray-500 mt-1">Optional. Leave blank for natural size (capped at email width).</div>
        </label>
      </div>
    </Modal>
  );
}
